import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "icon", "count", "favoritesContainer", "favoritesSection"]
  static values = { 
    propertyId: Number,
    favorited: Boolean,
    url: String,
    favoritesUrl: String
  }

  connect() {
    this.updateButtonState()
    
    // Listen for favorites changes from other controller instances
    document.addEventListener('favorites:changed', this.handleFavoritesChanged.bind(this))
  }

  disconnect() {
    document.removeEventListener('favorites:changed', this.handleFavoritesChanged.bind(this))
  }

  toggle(event) {
    event.preventDefault()
    
    const formData = new FormData()
    formData.append('property_id', this.propertyIdValue)
    
    // Get CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      formData.append('authenticity_token', csrfToken)
    }

    fetch(this.urlValue, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.favorited !== undefined) {
        this.favoritedValue = data.favorited
        this.updateButtonState()
        this.updateFavoriteCount(data.favorite_count)
        this.showToast(data.message)
        this.updateFavoritesContainer()
        this.refreshFavoritesSection()
        this.dispatchFavoritesChangedEvent()
      }
    })
    .catch(error => {
      console.error('Error:', error)
      this.showToast('Error updating favorite status', 'error')
    })
  }

  updateButtonState() {
    if (this.hasIconTarget) {
      if (this.favoritedValue) {
        this.iconTarget.classList.add('text-danger')
        this.iconTarget.classList.remove('text-muted')
        this.buttonTarget.title = "Remove from favorites"
      } else {
        this.iconTarget.classList.remove('text-danger')
        this.iconTarget.classList.add('text-muted')
        this.buttonTarget.title = "Add to favorites"
      }
    }
  }

  updateFavoriteCount(count) {
    if (this.hasCountTarget && count !== undefined) {
      this.countTarget.textContent = count
    }
  }

  updateFavoritesContainer() {
    // If we're on the favorites page and this property was unfavorited, remove it
    if (this.hasFavoritesContainerTarget && !this.favoritedValue) {
      const propertyCard = this.element.closest('.col-md-6, .col-lg-4, .col-lg-3')
      if (propertyCard) {
        propertyCard.style.transition = 'opacity 0.3s ease'
        propertyCard.style.opacity = '0'
        setTimeout(() => {
          propertyCard.remove()
          this.checkEmptyFavorites()
        }, 300)
      }
    }
  }

  checkEmptyFavorites() {
    // Check if there are any remaining property cards
    const remainingCards = this.favoritesContainerTarget.querySelectorAll('.col-md-6, .col-lg-4, .col-lg-3')
    if (remainingCards.length === 0) {
      // Show empty state
      this.showEmptyState()
    }
  }

  showEmptyState() {
    const emptyStateHTML = `
      <div class="col-12">
        <div class="card">
          <div class="card-body text-center py-5">
            <i class="fas fa-heart fa-4x text-muted mb-4"></i>
            <h2>No favorite properties yet</h2>
            <p class="text-muted mb-4">
              Start exploring amazing hunting properties and save your favorites for easy access later!
            </p>
            <div class="d-flex gap-3 justify-content-center">
              <a href="/properties" class="btn btn-primary btn-lg">Browse Properties</a>
              <a href="/leases" class="btn btn-outline-primary">Featured Properties</a>
            </div>
          </div>
        </div>
      </div>
    `
    this.favoritesContainerTarget.innerHTML = emptyStateHTML
  }

  refreshFavoritesSection() {
    // Only refresh if we're on the leases page and have a favorites section
    if (!this.hasFavoritesSectionTarget) return

    // Get the current user's favorites section content
    if (this.favoritesUrlValue) {
      fetch(this.favoritesUrlValue, {
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(response => response.text())
      .then(html => {
        // Parse the HTML to get just the favorites section content
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')
        const newFavoritesContent = doc.querySelector('.row[data-favorites-target="favoritesContainer"]') || 
                                   doc.querySelector('.card') // fallback for empty state
        
        if (newFavoritesContent) {
          this.favoritesSectionTarget.innerHTML = newFavoritesContent.outerHTML
          // Update stats counter too
          this.updateStatsCounter()
        }
      })
      .catch(error => {
        console.error('Error refreshing favorites section:', error)
      })
    }
  }

  updateStatsCounter() {
    // Update the favorites count in the stats section
    const statsCard = document.querySelector('.bg-warning h4')
    if (statsCard) {
      fetch('/leases.json', {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(response => response.json())
      .then(data => {
        if (data.stats && data.stats.favorite_properties_count !== undefined) {
          statsCard.textContent = data.stats.favorite_properties_count
        }
      })
      .catch(error => {
        console.error('Error updating stats counter:', error)
      })
    }
  }

  dispatchFavoritesChangedEvent() {
    // Dispatch a custom event that other controllers can listen to
    const event = new CustomEvent('favorites:changed', {
      detail: { propertyId: this.propertyIdValue }
    })
    document.dispatchEvent(event)
  }

  handleFavoritesChanged(event) {
    // Only refresh if we have a favorites section and this isn't the controller that triggered the change
    if (this.hasFavoritesSectionTarget && event.detail.propertyId !== this.propertyIdValue) {
      this.refreshFavoritesSection()
    }
  }

  showToast(message, type = 'success') {
    // Create a simple toast notification
    const toast = document.createElement('div')
    toast.className = `alert alert-${type === 'error' ? 'danger' : 'success'} position-fixed`
    toast.style.cssText = `
      top: 20px;
      right: 20px;
      z-index: 1050;
      min-width: 300px;
      animation: slideIn 0.3s ease;
    `
    toast.textContent = message

    // Add animation styles if not already present
    if (!document.getElementById('toast-styles')) {
      const style = document.createElement('style')
      style.id = 'toast-styles'
      style.textContent = `
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOut {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
      `
      document.head.appendChild(style)
    }

    document.body.appendChild(toast)

    // Auto-remove after 3 seconds
    setTimeout(() => {
      toast.style.animation = 'slideOut 0.3s ease'
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast)
        }
      }, 300)
    }, 3000)
  }
}